// Einkaufsliste – der Bildschirm, der am häufigsten offen ist.
//
// Neu gegenüber der alten Fassung: Gruppierung nach Kategorie in der
// Reihenfolge des Ladenlayouts, Wischen zum Löschen mit Rückgängig, Notizen,
// geschätzte Summe, Teilen – und alles funktioniert offline.
import { findCategory, formatEuro, formatNumber } from '@speis/shared';
import type { ShoppingItem, ShoppingListResponse } from '@speis/shared';
import { api } from '../core/api.js';
import { guard, guarded } from '../core/guard.js';
import { queue } from '../core/outbox.js';
import { applyShopping, state, subscribe } from '../core/store.js';
import { animateReorder, append, clear, el, haptic, replace } from '../ui/dom.js';
import { confirmDialog } from '../ui/dialog.js';
import { icon } from '../ui/icons.js';
import { openSheet } from '../ui/sheet.js';
import { swipeToDelete } from '../ui/swipe.js';
import { toast } from '../ui/toast.js';
import { SOURCE_BADGE } from './shared.js';

let unsubscribe: (() => void) | null = null;
let host: HTMLElement | null = null;

export function renderShopping(container: HTMLElement): void {
  host = container;
  unsubscribe?.();

  clear(container);
  container.append(buildAddForm(), el('div', { class: 'shopping-body' }));
  draw();

  unsubscribe = subscribe((changed) => {
    if (changed.has('shopping') || changed.has('estimatedTotal') || changed.has('products')) draw();
  });
}

export function teardownShopping(): void {
  unsubscribe?.();
  unsubscribe = null;
  host = null;
}

// ── Eingabe ───────────────────────────────────────────────────────────

function buildAddForm(): HTMLElement {
  const input = el('input', {
    class: 'input',
    type: 'text',
    id: 'add-input',
    placeholder: 'Etwas hinzufügen …',
    enterkeyhint: 'done',
    autocomplete: 'off',
    'aria-label': 'Artikel hinzufügen',
    'aria-describedby': 'add-hint',
  });

  const suggestions = el('div', { class: 'suggest', role: 'listbox', hidden: true });

  const form = el('form', { class: 'add-form' }, [
    el('div', { class: 'add-row' }, [
      input,
      el(
        'button',
        { class: 'btn btn-primary add-btn', type: 'submit', 'aria-label': 'Hinzufügen' },
        icon('plus', { size: 22 }),
      ),
    ]),
    el('span', { class: 'sr-only', id: 'add-hint' }, 'Vorschläge erscheinen beim Tippen.'),
    suggestions,
  ]);

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const value = input.value.trim();
    if (!value) return;
    input.value = '';
    hideSuggestions(suggestions);
    void addItem(value);
  });

  input.addEventListener('input', () => updateSuggestions(input, suggestions));
  input.addEventListener('blur', () => window.setTimeout(() => hideSuggestions(suggestions), 200));

  return form;
}

function hideSuggestions(box: HTMLElement): void {
  box.hidden = true;
  clear(box);
}

/**
 * Vorschläge: erst Katalogtreffer, sortiert nach Kaufhäufigkeit statt nur
 * nach Alphabet – was man ständig kauft, soll oben stehen.
 */
function updateSuggestions(input: HTMLInputElement, box: HTMLElement): void {
  const query = input.value.trim().toLowerCase();
  if (query.length < 2) {
    hideSuggestions(box);
    return;
  }

  const onList = new Set(state.shopping.filter((i) => !i.checked).map((i) => i.productId));
  const hits = state.products
    .filter((p) => !onList.has(p.id))
    .filter((p) => `${p.name} ${p.brand ?? ''}`.toLowerCase().includes(query))
    .sort((a, b) => {
      const aStarts = a.name.toLowerCase().startsWith(query) ? 0 : 1;
      const bStarts = b.name.toLowerCase().startsWith(query) ? 0 : 1;
      if (aStarts !== bStarts) return aStarts - bStarts;
      return a.name.localeCompare(b.name, 'de');
    })
    .slice(0, 6);

  if (!hits.length) {
    hideSuggestions(box);
    return;
  }

  replace(
    box,
    hits.map((p) =>
      el(
        'button',
        {
          class: 'suggest-item',
          type: 'button',
          role: 'option',
          onclick: () => {
            input.value = '';
            hideSuggestions(box);
            void addItem(p.name, p.id);
          },
        },
        [
          el('span', { class: 'suggest-name' }, p.brand ? `${p.name} · ${p.brand}` : p.name),
          p.closedCount > 0 ? el('span', { class: 'tiny' }, `${p.closedCount}× da`) : null,
        ],
      ),
    ),
  );
  box.hidden = false;
}

async function addItem(text: string, productId?: number): Promise<void> {
  // Exakter Katalogtreffer wird verknüpft – dann räumt der Bon-Scan ihn später
  // automatisch ab und der Preis ist bekannt.
  const match =
    productId ?? state.products.find((p) => p.name.toLowerCase() === text.toLowerCase())?.id;

  const optimistic: ShoppingItem = {
    id: -Date.now(),
    productId: match ?? null,
    name: match ? (state.products.find((p) => p.id === match)?.name ?? text) : text,
    brand: null,
    category: match ? (state.products.find((p) => p.id === match)?.category ?? null) : null,
    amount: null,
    unit: null,
    source: 'manual',
    checked: false,
    note: null,
    lastPrice: match ? (state.products.find((p) => p.id === match)?.lastPrice ?? null) : null,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  applyShopping({ items: [...state.shopping, optimistic], estimatedTotal: state.estimatedTotal });
  haptic(8);

  const result = await guard(
    () => queue('shopping.add', match ? { productId: match } : { freeText: text }),
    { failure: 'Konnte nicht hinzufügen' },
  );

  if (result?.shopping) applyShopping(result.shopping);
  else if (!result)
    applyShopping({
      items: state.shopping.filter((i) => i.id !== optimistic.id),
      estimatedTotal: state.estimatedTotal,
    });
}

// ── Darstellung ───────────────────────────────────────────────────────

function draw(): void {
  const body = host?.querySelector<HTMLElement>('.shopping-body');
  if (!body) return;

  const open = state.shopping.filter((i) => !i.checked);
  const done = state.shopping.filter((i) => i.checked);

  animateReorder(body, () => {
    clear(body);

    if (!state.shopping.length) {
      body.append(emptyState());
      return;
    }

    if (open.length) {
      for (const [category, items] of groupByCategory(open)) {
        body.append(categoryHeading(category, items.length));
        const list = el('ul', { class: 'list' });
        for (const item of items) list.append(renderRow(item));
        body.append(list);
      }
      body.append(summaryBar(open));
    } else {
      body.append(allDoneState());
    }

    if (done.length) body.append(doneSection(done));
  });
}

/** Gruppiert nach Kategorie; die Reihenfolge liefert bereits der Server. */
function groupByCategory(items: ShoppingItem[]): Map<string, ShoppingItem[]> {
  const groups = new Map<string, ShoppingItem[]>();
  for (const item of items) {
    const key = findCategory(item.category).key;
    const list = groups.get(key) ?? [];
    list.push(item);
    groups.set(key, list);
  }
  return groups;
}

function categoryHeading(categoryKey: string, count: number): HTMLElement {
  const category = findCategory(categoryKey);
  return el('h2', { class: 'cat-head' }, [
    el('span', { class: 'cat-icon', 'aria-hidden': 'true' }, category.icon),
    el('span', {}, category.label),
    el('span', { class: 'cat-count tiny' }, String(count)),
  ]);
}

function renderRow(item: ShoppingItem): HTMLElement {
  const check = el(
    'button',
    {
      class: 'check',
      type: 'button',
      role: 'checkbox',
      'aria-checked': String(item.checked),
      'aria-label': `${item.name} abhaken`,
      onclick: () => void toggle(item),
    },
    icon('check', { size: 16 }),
  );

  const label = item.amount
    ? `${formatNumber(item.amount)}${item.unit ? ` ${item.unit}` : ''} ${item.name}`
    : item.name;

  const meta = el('div', { class: 'item-meta' });
  const badge = SOURCE_BADGE[item.source];
  if (badge) meta.append(el('span', { class: `badge ${badge.class}` }, badge.label));
  if (item.note) meta.append(el('span', {}, item.note));
  if (item.lastPrice != null) {
    meta.append(el('span', { class: 'tabular' }, `ca. ${formatEuro(item.lastPrice)}`));
  }

  const body = el(
    'button',
    {
      class: 'item-body item-open',
      type: 'button',
      onclick: () => openItemSheet(item),
    },
    [el('div', { class: 'item-name' }, label), meta.childElementCount ? meta : null],
  );

  const row = el(
    'li',
    {
      class: `list-item${item.checked ? ' is-checked' : ''}`,
      'data-id': item.id,
    },
    [check, body],
  );

  // Wischen zum Löschen gibt es nur für echte (gespeicherte) Einträge.
  if (item.id > 0) {
    return el(
      'li',
      { class: 'list-row' },
      swipeToDelete(row, { onDelete: () => void remove(item) }),
    );
  }
  return row;
}

function summaryBar(open: ShoppingItem[]): HTMLElement {
  return el('div', { class: 'summary-bar' }, [
    el('span', {}, `${open.length} ${open.length === 1 ? 'Eintrag' : 'Einträge'}`),
    state.estimatedTotal != null
      ? el('span', { class: 'tabular' }, `geschätzt ${formatEuro(state.estimatedTotal)}`)
      : null,
    el('button', { class: 'btn-ghost btn btn-sm', type: 'button', onclick: shareList }, [
      icon('share', { size: 16 }),
      'Teilen',
    ]),
  ]);
}

function doneSection(done: ShoppingItem[]): HTMLElement {
  const list = el('ul', { class: 'list' });
  for (const item of done) list.append(renderRow(item));

  return el('details', { class: 'done-section', open: done.length <= 4 }, [
    el('summary', {}, [
      el('span', {}, `${done.length} erledigt`),
      el(
        'button',
        {
          class: 'btn btn-sm btn-ghost',
          type: 'button',
          onclick: guarded(clearChecked, { failure: 'Aufräumen fehlgeschlagen' }),
        },
        'Entfernen',
      ),
    ]),
    list,
  ]);
}

function emptyState(): HTMLElement {
  return el('div', { class: 'empty' }, [
    el('div', { class: 'empty-icon', 'aria-hidden': 'true' }, '🛒'),
    el('p', { class: 'empty-title' }, 'Die Liste ist leer'),
    el(
      'p',
      { class: 'empty-text' },
      'Tippe oben ein, was fehlt – oder lass die App aus Meldebestand und Wochenplan selbst nachfüllen.',
    ),
    el(
      'button',
      {
        class: 'btn btn-secondary',
        type: 'button',
        onclick: guarded(
          async () => {
            await api('/api/shopping/refresh-auto', { method: 'POST', body: {} });
          },
          { success: 'Liste aktualisiert' },
        ),
      },
      [icon('refresh', { size: 18 }), 'Automatisch füllen'],
    ),
  ]);
}

function allDoneState(): HTMLElement {
  return el('div', { class: 'empty' }, [
    el('div', { class: 'empty-icon', 'aria-hidden': 'true' }, '✅'),
    el('p', { class: 'empty-title' }, 'Alles eingekauft'),
    el(
      'p',
      { class: 'empty-text' },
      'Nichts mehr offen. Bon scannen (＋), dann wird alles verbucht.',
    ),
  ]);
}

// ── Aktionen ──────────────────────────────────────────────────────────

async function toggle(item: ShoppingItem): Promise<void> {
  if (item.id < 0) return; // noch nicht gespeichert
  haptic(item.checked ? 6 : 14);

  applyShopping({
    items: state.shopping.map((i) => (i.id === item.id ? { ...i, checked: !i.checked } : i)),
    estimatedTotal: state.estimatedTotal,
  });

  const result = await guard(
    () => queue('shopping.patch', { id: item.id, checked: !item.checked }),
    { failure: 'Synchronisierung fehlgeschlagen' },
  );
  if (result?.shopping) applyShopping(result.shopping);
}

async function remove(item: ShoppingItem): Promise<void> {
  const previous = state.shopping;
  applyShopping({
    items: state.shopping.filter((i) => i.id !== item.id),
    estimatedTotal: state.estimatedTotal,
  });
  haptic(10);

  let undone = false;
  toast(`${item.name} entfernt`, {
    action: {
      label: 'Rückgängig',
      onClick: () => {
        undone = true;
        applyShopping({ items: previous, estimatedTotal: state.estimatedTotal });
        void guard(() => queue('shopping.add', restorePayload(item)), {
          failure: 'Wiederherstellen fehlgeschlagen',
        });
      },
    },
    ms: 4500,
  });

  const result = await guard(() => queue('shopping.delete', { id: item.id }), {
    failure: 'Löschen fehlgeschlagen',
  });
  if (result?.shopping && !undone) applyShopping(result.shopping);
}

function restorePayload(item: ShoppingItem): Record<string, unknown> {
  return item.productId
    ? { productId: item.productId, amount: item.amount, unit: item.unit, note: item.note }
    : { freeText: item.name, amount: item.amount, unit: item.unit, note: item.note };
}

async function clearChecked(): Promise<void> {
  const result = await queue('shopping.clearChecked', {});
  if (result.shopping) applyShopping(result.shopping);
  else
    applyShopping({
      items: state.shopping.filter((i) => !i.checked),
      estimatedTotal: state.estimatedTotal,
    });
}

async function shareList(): Promise<void> {
  const { text } = await api<{ text: string }>('/api/shopping/text');
  if (navigator.share) {
    try {
      await navigator.share({ title: 'Einkaufsliste', text });
      return;
    } catch {
      // Abgebrochen oder nicht erlaubt – dann eben in die Zwischenablage.
    }
  }
  await navigator.clipboard?.writeText(text);
  toast('Liste in die Zwischenablage kopiert');
}

// ── Eintrag bearbeiten ────────────────────────────────────────────────

function openItemSheet(item: ShoppingItem): void {
  if (item.id < 0) return;

  const amount = el('input', {
    class: 'input',
    type: 'text',
    inputmode: 'decimal',
    value: item.amount ?? '',
    placeholder: 'Menge',
  });
  const unit = unitSelect(item.unit ?? '');
  const note = el('input', {
    class: 'input',
    type: 'text',
    value: item.note ?? '',
    placeholder: 'z.B. „die große Packung"',
  });
  const name = item.productId
    ? null
    : el('input', { class: 'input', type: 'text', value: item.name });

  const body = el('div', {}, [
    name
      ? el('div', { class: 'field' }, [el('label', {}, 'Bezeichnung'), name])
      : el('p', { class: 'muted' }, item.name),
    el('div', { class: 'field' }, [
      el('label', {}, 'Menge'),
      el('div', { class: 'input-row' }, [amount, unit]),
    ]),
    el('div', { class: 'field' }, [el('label', {}, 'Notiz'), note]),
  ]);

  const sheet = openSheet(body, { title: 'Eintrag bearbeiten' });

  sheet.addActions(
    el(
      'button',
      {
        class: 'btn btn-primary btn-block',
        type: 'button',
        onclick: guarded(
          async () => {
            const payload: Record<string, unknown> = {
              id: item.id,
              amount: amount.value.trim() === '' ? null : Number(amount.value.replace(',', '.')),
              unit: unit.value || null,
              note: note.value.trim() || null,
            };
            if (name) payload.freeText = name.value.trim() || item.name;
            const result = await queue('shopping.patch', payload);
            if (result.shopping) applyShopping(result.shopping);
            sheet.close();
          },
          { success: 'Gespeichert', failure: 'Speichern fehlgeschlagen' },
        ),
      },
      'Speichern',
    ),
    el(
      'button',
      {
        class: 'btn btn-danger btn-block',
        type: 'button',
        onclick: async () => {
          const ok = await confirmDialog({
            title: `„${item.name}" entfernen?`,
            confirmLabel: 'Entfernen',
            danger: true,
          });
          if (!ok) return;
          sheet.close();
          void remove(item);
        },
      },
      'Entfernen',
    ),
  );
}

const UNIT_OPTIONS = ['', 'Stück', 'Pkg.', 'g', 'kg', 'ml', 'l', 'Dose', 'Packung', 'Bund'];

function unitSelect(value: string): HTMLSelectElement {
  const select = el('select', { class: 'select', 'aria-label': 'Einheit' });
  for (const option of UNIT_OPTIONS) {
    const node = el('option', { value: option }, option || '—');
    if (option === value) node.selected = true;
    select.append(node);
  }
  return select;
}

/** Frisch geladene Liste vom Server übernehmen (beim Screen-Eintritt). */
export async function refreshShopping(): Promise<void> {
  const data = await api<ShoppingListResponse>('/api/shopping');
  applyShopping(data);
}

export { append };
